/**
 * Regras de instalação por tipologia de mobiliário.
 *
 * O prompt do meX ensina *onde* uma peça vai; o catálogo diz *quanto* ela
 * mede. Faltava o terceiro dado, que nem prompt nem catálogo carregavam: as
 * relações de instalação que um projetista aplica sem pensar — um suspenso é
 * fixado na parede ou no teto e nasce com vão livre sobre o que estiver abaixo
 * (módulo superior sobre bancada, espelho sobre bancada de banheiro, coifa
 * sobre cocção, pendente sobre mesa), e um empilhamento só existe quando o
 * briefing pede uma torre. Este módulo materializa esse
 * conhecimento como dado do produto, para que planejador, executor, revisor,
 * ferramentas de implantação e validador do plano usem exatamente a mesma
 * régua, em vez de depender da memória do modelo.
 *
 * As faixas são deliberadamente conservadoras: o mínimo marca o ponto abaixo
 * do qual a solução deixa de ser profissional (defeito), a faixa típica é
 * orientação de projeto. Nada aqui define medida de móvel — só relação.
 */

export type InstallationTypology =
  | "upper_cabinet"
  | "shelf"
  | "wall_panel"
  | "mirror_or_art"
  | "hood"
  | "lighting"
  | "base_cabinet"
  | "countertop"
  | "low_furniture"
  | "table"
  | "tall_unit"
  | "appliance"
  | "seating"
  | "bed"
  | "fixture"
  | "unknown";

export type TypologyMount = "wall" | "floor" | "ceiling" | "element" | "unknown";

export interface TypologyClassification {
  kind: InstallationTypology;
  label: string;
  mount: TypologyMount;
  confidence: "high" | "medium" | "low";
  matchedTerms: string[];
  /** O que sustentou a classificação: a linguagem do ofício, o estado de instalação observado na cena ou apenas a proporção do volume. */
  basis?: "language" | "installation" | "proportion" | "none";
}

/**
 * Estado de instalação observado na cena. Um elemento "Módulo 1" fixado na
 * parede a 1.960 mm do piso é um suspenso, seja qual for o nome; um "Aéreo"
 * apoiado no piso continua sendo um aéreo (mal instalado). A linguagem decide
 * a tipologia específica; o estado decide a família quando a linguagem cala.
 */
export interface ObservedInstallation {
  support: "wall" | "floor" | "ceiling" | "element" | "unconstrained" | "unknown";
  /** Altura da face inferior do elemento em relação ao piso do nível (mm). */
  bottomHeightMm?: number | null;
  topHeightMm?: number | null;
  /** O elemento não toca o piso. */
  floating?: boolean;
  /** O elemento encosta ou quase encosta numa parede. */
  wallBacked?: boolean;
}

export interface TypologyDescriptor {
  name?: string | null;
  category?: string | null;
  description?: string | null;
  tags?: ReadonlyArray<string> | null;
  dimensionsMm?: { width: number; height: number; depth: number } | null;
  installation?: ObservedInstallation | null;
}

/** Base above this height, without contact with the floor, reads as a suspended element. */
export const SUSPENDED_BASE_HEIGHT_MM = 1_000;

export interface VerticalClearanceRule {
  /** Identificador estável da regra aplicada (chave da tabela VERTICAL_CLEARANCE_RULES). */
  rule: string;
  /** Abaixo disto a relação é defeito de instalação, não preferência. */
  minimumMm: number;
  /** Faixa em que um projetista normalmente trabalha. */
  typicalMm: [number, number];
  /** Altura recomendada da face inferior do elemento superior em relação ao piso, quando existe prática consolidada. */
  bottomHeightMm: [number, number] | null;
  /** Altura máxima recomendada do topo do elemento superior (alcance), quando aplicável. */
  maximumTopHeightMm: number | null;
  /** Contato físico só é aceitável em empilhamento declarado com apoio no elemento. */
  contactAllowed: boolean;
  summary: string;
}

const UPPER_TERMS = ["aereo", "aereos", "superior", "superiores", "suspenso", "suspensa", "suspensos", "basculante", "basculantes"];
const SHELF_TERMS = ["prateleira", "prateleiras", "nicho", "nichos"];
const WALL_PANEL_TERMS = ["painel", "paineis", "ripado", "lambri", "cabeceira estofada"];
const MIRROR_OR_ART_TERMS = ["espelho", "espelhos", "quadro", "quadros", "tela", "obra", "poster", "moldura"];
const HOOD_TERMS = ["coifa", "coifas", "depurador", "exaustor"];
const LIGHTING_TERMS = ["pendente", "pendentes", "lustre", "lustres", "luminaria", "luminarias", "plafon", "spot", "arandela", "trilho"];
const COUNTERTOP_TERMS = ["bancada", "bancadas", "tampo", "pia", "cooktop", "cuba", "ilha", "peninsula"];
const BASE_TERMS = ["balcao", "balcoes", "gabinete", "gabinetes", "base", "bases", "inferior", "inferiores", "gaveteiro", "gaveteiros"];
const LOW_FURNITURE_TERMS = ["aparador", "buffet", "bufe", "rack", "comoda", "criado", "criado mudo", "baixo", "baixa", "banco baixo", "console"];
const TABLE_TERMS = ["mesa", "mesas", "escrivaninha", "bancada de trabalho", "bancada de estudo", "penteadeira", "balcao de atendimento"];
const TALL_TERMS = ["torre", "torres", "paneleiro", "roupeiro", "guarda-roupa", "guarda roupa", "armario alto", "coluna", "despensa", "closet", "estante"];
const APPLIANCE_TERMS = ["geladeira", "refrigerador", "fogao", "forno", "micro-ondas", "microondas", "lava-loucas", "lava loucas", "maquina de lavar", "lavadora", "secadora", "adega", "freezer"];
const SEATING_TERMS = ["sofa", "poltrona", "cadeira", "banco", "banqueta", "pufe", "chaise"];
const BED_TERMS = ["cama", "camas", "berco", "beliche", "colchao"];
const FIXTURE_TERMS = ["tomada", "interruptor", "ponto de luz", "ponto eletrico", "cortina", "persiana", "registro", "torneira"];

const CATEGORY_HINTS: Array<{ pattern: RegExp; kind: InstallationTypology }> = [
  { pattern: /aereos?|superior/, kind: "upper_cabinet" },
  { pattern: /prateleira|nicho/, kind: "shelf" },
  { pattern: /painel|ripado/, kind: "wall_panel" },
  { pattern: /espelho|quadro|arte|decoracao de parede/, kind: "mirror_or_art" },
  { pattern: /coifa|exaust/, kind: "hood" },
  { pattern: /iluminacao|luminaria|pendente/, kind: "lighting" },
  { pattern: /bancada|tampo/, kind: "countertop" },
  { pattern: /balc(?:ao|oes)|gabinete|bases?\b|inferior/, kind: "base_cabinet" },
  { pattern: /mesa|escrivaninha/, kind: "table" },
  { pattern: /torre|paneleiro|roupeiro|closet|armario alto|estante/, kind: "tall_unit" },
  { pattern: /eletro|eletrodomestico|geladeira|fogao|forno/, kind: "appliance" },
  { pattern: /sofa|poltrona|cadeira|banco|assento/, kind: "seating" },
  { pattern: /cama|berco|colchao/, kind: "bed" },
  { pattern: /eletrica|acessorio/, kind: "fixture" },
];

const LABELS: Record<InstallationTypology, string> = {
  upper_cabinet: "aéreo / módulo superior",
  shelf: "prateleira / nicho",
  wall_panel: "painel de parede",
  mirror_or_art: "espelho / quadro",
  hood: "coifa / exaustor",
  lighting: "luminária / pendente",
  base_cabinet: "balcão / módulo base",
  countertop: "bancada / tampo",
  low_furniture: "móvel baixo",
  table: "mesa / bancada de trabalho",
  tall_unit: "torre / armário alto",
  appliance: "eletrodoméstico",
  seating: "assento",
  bed: "cama",
  fixture: "acessório / ponto de instalação",
  unknown: "tipologia não identificada",
};

const MOUNTS: Record<InstallationTypology, TypologyMount> = {
  upper_cabinet: "wall",
  shelf: "wall",
  wall_panel: "wall",
  mirror_or_art: "wall",
  hood: "wall",
  lighting: "ceiling",
  base_cabinet: "floor",
  countertop: "element",
  low_furniture: "floor",
  table: "floor",
  tall_unit: "floor",
  appliance: "floor",
  seating: "floor",
  bed: "floor",
  fixture: "unknown",
  unknown: "unknown",
};

export function normalizeTypologyText(value: string | null | undefined): string {
  return (value ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLocaleLowerCase("pt-BR")
    .replace(/[^a-z0-9]+/g, " ")
    .trim();
}

function findTerms(text: string, terms: ReadonlyArray<string>): string[] {
  if (!text) return [];
  return terms.filter((term) => new RegExp(`(?:^|\\s)${term.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}(?:\\s|$)`).test(text));
}

/**
 * Classifica um elemento pela linguagem do ofício (nome, categoria, descrição,
 * etiquetas) e, em último caso, pela proporção do volume. A confiança diz ao
 * chamador quanto pode exigir com base na classificação.
 */
export function classifyInstallationTypology(descriptor: TypologyDescriptor): TypologyClassification {
  const name = normalizeTypologyText(descriptor.name);
  const category = normalizeTypologyText(descriptor.category);
  const description = normalizeTypologyText(descriptor.description);
  const tags = (descriptor.tags ?? []).map((tag) => normalizeTypologyText(tag)).join(" ");
  const primary = `${name} ${tags}`.trim();
  const secondary = `${category} ${description}`.trim();

  const attempt = (kind: InstallationTypology, terms: ReadonlyArray<string>): TypologyClassification | null => {
    const primaryHits = findTerms(primary, terms);
    if (primaryHits.length) return { kind, label: LABELS[kind], mount: MOUNTS[kind], confidence: "high", matchedTerms: primaryHits, basis: "language" };
    const secondaryHits = findTerms(secondary, terms);
    if (secondaryHits.length) return { kind, label: LABELS[kind], mount: MOUNTS[kind], confidence: "medium", matchedTerms: secondaryHits, basis: "language" };
    return null;
  };

  // Ordem importa: "aéreo basculante" e "armário superior" vencem termos
  // genéricos como "base" ou "baixo" que também aparecem em descrições.
  const ordered: Array<[InstallationTypology, ReadonlyArray<string>]> = [
    ["upper_cabinet", UPPER_TERMS],
    ["hood", HOOD_TERMS],
    ["tall_unit", TALL_TERMS],
    ["shelf", SHELF_TERMS],
    ["wall_panel", WALL_PANEL_TERMS],
    ["mirror_or_art", MIRROR_OR_ART_TERMS],
    ["lighting", LIGHTING_TERMS],
    ["appliance", APPLIANCE_TERMS],
    ["countertop", COUNTERTOP_TERMS],
    ["bed", BED_TERMS],
    ["seating", SEATING_TERMS],
    ["fixture", FIXTURE_TERMS],
    ["table", TABLE_TERMS],
    ["base_cabinet", BASE_TERMS],
    ["low_furniture", LOW_FURNITURE_TERMS],
  ];
  for (const [kind, terms] of ordered) {
    const primaryHits = findTerms(primary, terms);
    if (primaryHits.length) return { kind, label: LABELS[kind], mount: MOUNTS[kind], confidence: "high", matchedTerms: primaryHits, basis: "language" };
  }
  for (const hint of CATEGORY_HINTS) {
    if (hint.pattern.test(category)) {
      return { kind: hint.kind, label: LABELS[hint.kind], mount: MOUNTS[hint.kind], confidence: "medium", matchedTerms: [category], basis: "language" };
    }
  }
  for (const [kind, terms] of ordered) {
    const found = attempt(kind, terms);
    if (found) return { ...found, basis: "language" };
  }

  // Sem linguagem, o estado de instalação observado decide a família: um
  // volume fixado na parede (ou no teto) e sem contato com o piso é um
  // suspenso, seja qual for o nome de catálogo que ele carregue.
  const observed = classifyByObservedInstallation(descriptor);
  if (observed) return observed;

  const dimensions = descriptor.dimensionsMm;
  if (dimensions && dimensions.width > 0 && dimensions.height > 0 && dimensions.depth > 0) {
    // Sem linguagem nem estado, a proporção ainda separa o que é caixa de piso
    // do que é volume alto. Confiança baixa: serve para orientar, não para
    // reprovar. A suspended kind is never inferred from proportions alone: a
    // small box can be a pouf or a bedside unit.
    if (dimensions.height >= 1_800) return { kind: "tall_unit", label: LABELS.tall_unit, mount: "floor", confidence: "low", matchedTerms: [], basis: "proportion" };
    if (dimensions.height <= 950 && dimensions.depth >= 400) {
      return { kind: "low_furniture", label: LABELS.low_furniture, mount: "floor", confidence: "low", matchedTerms: [], basis: "proportion" };
    }
  }
  return { kind: "unknown", label: LABELS.unknown, mount: "unknown", confidence: "low", matchedTerms: [], basis: "none" };
}

/**
 * Família pelo estado observado. Devolve null quando o estado não é
 * conclusivo (sem dado de instalação, ou apoiado no piso sem proporção
 * reconhecível — aí a proporção decide).
 */
export function classifyByObservedInstallation(descriptor: TypologyDescriptor): TypologyClassification | null {
  const installation = descriptor.installation;
  if (!installation) return null;
  const dimensions = descriptor.dimensionsMm ?? null;
  const bottom = installation.bottomHeightMm ?? null;
  const floating = installation.floating ?? (bottom !== null ? bottom > 50 : false);
  const highEnough = bottom !== null && bottom >= SUSPENDED_BASE_HEIGHT_MM;
  const mountedOnWall = installation.support === "wall";
  const mountedOnCeiling = installation.support === "ceiling";
  const suspended = floating && (mountedOnWall || mountedOnCeiling || (highEnough && installation.wallBacked !== false));
  if (suspended) {
    const term = mountedOnCeiling ? "installation:ceiling-floating" : mountedOnWall ? "installation:wall-floating" : "installation:floating-high";
    if (mountedOnCeiling && dimensions && dimensions.width <= 800 && dimensions.depth <= 800 && dimensions.height <= 1_200) {
      return { kind: "lighting", label: LABELS.lighting, mount: "ceiling", confidence: "medium", matchedTerms: [term], basis: "installation" };
    }
    if (dimensions && dimensions.depth > 0 && dimensions.depth <= 80) {
      const flat = dimensions.width < 1_600 && dimensions.height < 1_600 ? "mirror_or_art" : "wall_panel";
      return { kind: flat, label: LABELS[flat], mount: "wall", confidence: "medium", matchedTerms: [term], basis: "installation" };
    }
    if (dimensions && dimensions.height > 0 && dimensions.height < 200) {
      return { kind: "shelf", label: LABELS.shelf, mount: "wall", confidence: "medium", matchedTerms: [term], basis: "installation" };
    }
    return { kind: "upper_cabinet", label: LABELS.upper_cabinet, mount: mountedOnCeiling ? "ceiling" : "wall", confidence: "medium", matchedTerms: [term], basis: "installation" };
  }
  if (installation.support === "floor" && dimensions && dimensions.height > 0) {
    if (dimensions.height >= 1_800) return { kind: "tall_unit", label: LABELS.tall_unit, mount: "floor", confidence: "medium", matchedTerms: ["installation:floor"], basis: "installation" };
    if (dimensions.height <= 950) return { kind: "low_furniture", label: LABELS.low_furniture, mount: "floor", confidence: "medium", matchedTerms: ["installation:floor"], basis: "installation" };
  }
  return null;
}

/** Se o estado observado descreve um elemento suspenso (fixado e sem contato com o piso). */
export function isSuspendedInstallation(installation: ObservedInstallation | null | undefined): boolean {
  if (!installation) return false;
  const bottom = installation.bottomHeightMm ?? null;
  const floating = installation.floating ?? (bottom !== null ? bottom > 50 : false);
  if (!floating) return false;
  return installation.support === "wall" || installation.support === "ceiling" || (bottom !== null && bottom >= SUSPENDED_BASE_HEIGHT_MM);
}

const SUSPENDED_KINDS: ReadonlySet<InstallationTypology> = new Set(["upper_cabinet", "shelf", "wall_panel", "mirror_or_art", "hood", "lighting"]);
const SUPPORTING_KINDS: ReadonlySet<InstallationTypology> = new Set(["base_cabinet", "countertop", "low_furniture", "table", "tall_unit", "appliance", "seating", "bed"]);

export function isSuspendedTypology(kind: InstallationTypology): boolean {
  return SUSPENDED_KINDS.has(kind);
}

export function isSupportingTypology(kind: InstallationTypology): boolean {
  return SUPPORTING_KINDS.has(kind);
}

interface VerticalRuleEntry {
  upper: ReadonlyArray<InstallationTypology> | "any-suspended";
  lower: ReadonlyArray<InstallationTypology> | "any-supporting-or-unknown";
  rule: VerticalClearanceRule;
}

/**
 * Régua de relações verticais por par de tipologias, em ordem de
 * especificidade. É uma tabela, não um caso: acrescentar uma combinação nova
 * (por exemplo, armário sobre máquina de lavar) é uma linha, sem tocar em
 * validador, ferramenta ou revisor. As faixas são práticas consolidadas de
 * marcenaria, interiores e instalações, deliberadamente conservadoras.
 */
export const VERTICAL_CLEARANCE_RULES: ReadonlyArray<VerticalRuleEntry> = [
  {
    upper: ["upper_cabinet"],
    lower: ["countertop", "base_cabinet"],
    rule: {
      rule: "upper_over_worktop",
      minimumMm: 450,
      typicalMm: [500, 650],
      bottomHeightMm: [1_350, 1_550],
      maximumTopHeightMm: 2_300,
      contactAllowed: false,
      summary: "Módulo superior sobre bancada ou balcão: fixado na parede, com face inferior entre 1.350 e 1.550 mm do piso e vão livre de 500 a 650 mm sobre o tampo (mínimo 450 mm); profundidade menor que a da base.",
    },
  },
  {
    upper: ["upper_cabinet"],
    lower: ["appliance"],
    rule: {
      rule: "upper_over_appliance",
      minimumMm: 30,
      typicalMm: [50, 150],
      bottomHeightMm: null,
      maximumTopHeightMm: 2_300,
      contactAllowed: false,
      summary: "Módulo superior sobre eletrodoméstico: fixado na parede, com folga de 50 a 150 mm para ventilação e retirada do equipamento (mínimo 30 mm); nunca apoiado nele.",
    },
  },
  {
    upper: ["upper_cabinet"],
    lower: ["low_furniture", "table", "seating", "bed", "unknown"],
    rule: {
      rule: "upper_over_low_furniture",
      minimumMm: 350,
      typicalMm: [400, 700],
      bottomHeightMm: [1_150, 1_550],
      maximumTopHeightMm: 2_300,
      contactAllowed: false,
      summary: "Módulo superior sobre móvel baixo, mesa ou assento: fixado na parede, com vão livre de 400 a 700 mm (mínimo 350 mm) e face inferior entre 1.150 e 1.550 mm do piso; nunca apoiado no móvel, salvo empilhamento pedido no briefing.",
    },
  },
  {
    upper: ["shelf"],
    lower: ["countertop", "base_cabinet", "low_furniture", "table", "seating", "bed", "appliance", "unknown"],
    rule: {
      rule: "shelf_over_base",
      minimumMm: 250,
      typicalMm: [300, 600],
      bottomHeightMm: null,
      maximumTopHeightMm: 2_300,
      contactAllowed: false,
      summary: "Prateleira ou nicho sobre outro móvel: fixado na parede com vão livre de 300 a 600 mm (mínimo 250 mm) para uso e limpeza da superfície abaixo.",
    },
  },
  {
    upper: ["hood"],
    lower: ["countertop", "appliance", "base_cabinet"],
    rule: {
      rule: "hood_over_cooktop",
      minimumMm: 600,
      typicalMm: [650, 750],
      bottomHeightMm: [1_500, 1_650],
      maximumTopHeightMm: null,
      contactAllowed: false,
      summary: "Coifa ou exaustor sobre cocção: vão livre de 650 a 750 mm acima do cooktop ou fogão (mínimo 600 mm), conforme o fabricante; face inferior em torno de 1.500 a 1.650 mm do piso.",
    },
  },
  {
    upper: ["lighting"],
    lower: ["table", "countertop", "low_furniture", "seating", "bed"],
    rule: {
      rule: "pendant_over_surface",
      minimumMm: 600,
      typicalMm: [650, 900],
      bottomHeightMm: null,
      maximumTopHeightMm: null,
      contactAllowed: false,
      summary: "Pendente ou luminária sobre mesa, bancada ou assento: face inferior de 650 a 900 mm acima da superfície (mínimo 600 mm), sem bloquear a visão entre pessoas sentadas.",
    },
  },
  {
    upper: ["mirror_or_art"],
    lower: ["countertop", "base_cabinet", "low_furniture", "table", "seating", "bed"],
    rule: {
      rule: "art_over_furniture",
      minimumMm: 50,
      typicalMm: [100, 300],
      bottomHeightMm: null,
      maximumTopHeightMm: null,
      contactAllowed: false,
      summary: "Espelho ou quadro sobre móvel: fixado na parede com vão de 100 a 300 mm acima do tampo ou do encosto (mínimo 50 mm), centrado no móvel e com o centro visual próximo à altura dos olhos.",
    },
  },
  {
    upper: ["wall_panel"],
    lower: ["low_furniture", "seating", "bed", "base_cabinet", "table", "unknown"],
    rule: {
      rule: "panel_over_furniture",
      minimumMm: 0,
      typicalMm: [0, 150],
      bottomHeightMm: null,
      maximumTopHeightMm: null,
      contactAllowed: true,
      summary: "Painel de parede atrás ou sobre móvel: pode encostar ou descer até o piso; quando começa acima do móvel, vão de até 150 mm para ler como conjunto.",
    },
  },
  {
    upper: ["countertop"],
    lower: ["base_cabinet", "appliance", "low_furniture"],
    rule: {
      rule: "countertop_on_base",
      minimumMm: 0,
      typicalMm: [0, 0],
      bottomHeightMm: null,
      maximumTopHeightMm: null,
      contactAllowed: true,
      summary: "Tampo ou bancada sobre balcão ou eletrodoméstico: apoia diretamente, sem vão.",
    },
  },
  {
    upper: "any-suspended",
    lower: "any-supporting-or-unknown",
    rule: {
      rule: "suspended_over_support",
      minimumMm: 50,
      typicalMm: [100, 700],
      bottomHeightMm: null,
      maximumTopHeightMm: null,
      contactAllowed: false,
      summary: "Elemento suspenso sobre outro móvel sem prática consolidada para esta combinação: fixado na parede ou no teto, sem contato e com folga funcional registrada no plano (mínimo 50 mm).",
    },
  },
];

const STACKED_RULE: VerticalClearanceRule = {
  rule: "stacked_module",
  minimumMm: 0,
  typicalMm: [0, 0],
  bottomHeightMm: null,
  maximumTopHeightMm: 2_300,
  contactAllowed: true,
  summary: "Módulo empilhado: apoia diretamente no elemento de baixo, com frentes e laterais alinhadas; só use quando o briefing, a referência ou o catálogo pedirem torre ou empilhamento.",
};

/**
 * Regra de folga vertical entre um elemento superior e o elemento abaixo dele.
 * Devolve null quando a combinação não tem prática consolidada — nesse caso o
 * contrato do plano continua sendo a única referência.
 */
export function verticalClearanceRule(input: {
  upper: InstallationTypology;
  lower: InstallationTypology;
  relation: "above" | "stacked" | "below";
  support?: TypologyMount | "auto" | "unconstrained" | null;
}): VerticalClearanceRule | null {
  if (input.relation === "stacked" || input.support === "element") return STACKED_RULE;
  for (const entry of VERTICAL_CLEARANCE_RULES) {
    const upperMatches = entry.upper === "any-suspended"
      ? isSuspendedTypology(input.upper)
      : entry.upper.includes(input.upper);
    const lowerMatches = entry.lower === "any-supporting-or-unknown"
      ? isSupportingTypology(input.lower) || input.lower === "unknown"
      : entry.lower.includes(input.lower);
    if (upperMatches && lowerMatches) return entry.rule;
  }
  return null;
}

export interface TypologyGuidance {
  targetTypology: TypologyClassification;
  referenceTypology: TypologyClassification | null;
  rule: VerticalClearanceRule | null;
  guidance: string;
}

/**
 * Orientação pronta para ser devolvida por ferramentas de implantação e para
 * mensagens do validador do plano — dados do sistema, não instrução externa.
 */
export function installationGuidance(input: {
  target: TypologyDescriptor;
  reference?: TypologyDescriptor | null;
  relation?: "above" | "below" | "stacked" | "beside" | "in_front_of" | "behind" | "against" | "aligned" | "centered" | "inside" | "custom" | null;
  support?: TypologyMount | "auto" | "unconstrained" | null;
}): TypologyGuidance {
  const targetTypology = classifyInstallationTypology(input.target);
  const referenceTypology = input.reference ? classifyInstallationTypology(input.reference) : null;
  let rule: VerticalClearanceRule | null = null;
  if (referenceTypology && (input.relation === "above" || input.relation === "stacked")) {
    rule = verticalClearanceRule({ upper: targetTypology.kind, lower: referenceTypology.kind, relation: input.relation, support: input.support ?? null });
  } else if (referenceTypology && input.relation === "below") {
    rule = verticalClearanceRule({ upper: referenceTypology.kind, lower: targetTypology.kind, relation: "above", support: input.support ?? null });
  }
  const parts: string[] = [];
  parts.push(`Alvo: ${targetTypology.label}${targetTypology.mount !== "unknown" ? ` (instalação: ${mountLabel(targetTypology.mount)})` : ""}.`);
  if (referenceTypology) parts.push(`Referência: ${referenceTypology.label}.`);
  if (rule) parts.push(rule.summary);
  else if (isSuspendedTypology(targetTypology.kind)) {
    parts.push(`Elemento suspenso: ${mountLabel(targetTypology.mount)}, com vão livre sobre o que estiver abaixo; contato direto só em empilhamento declarado com apoio no elemento.`);
  }
  return { targetTypology, referenceTypology, rule, guidance: parts.join(" ") };
}

export function mountLabel(mount: TypologyMount): string {
  switch (mount) {
    case "wall": return "fixado na parede";
    case "floor": return "apoiado no piso";
    case "ceiling": return "fixado no teto";
    case "element": return "apoiado em outro elemento";
    default: return "não determinada";
  }
}

export interface VerticalRelationAssessment {
  rule: VerticalClearanceRule | null;
  status: "ok" | "below_minimum" | "contact_not_allowed" | "no_rule";
  measuredGapMm: number | null;
  correctionMm: number | null;
  message: string | null;
}

/**
 * Avalia uma folga vertical medida (ou planejada) contra a régua profissional.
 * A correção sugerida é sempre para dentro da faixa típica — nunca para baixo do mínimo.
 */
export function assessVerticalRelation(input: {
  rule: VerticalClearanceRule | null;
  gapMm: number | null;
  touching?: boolean;
  upperLabel: string;
  lowerLabel: string;
}): VerticalRelationAssessment {
  const { rule } = input;
  if (!rule) return { rule: null, status: "no_rule", measuredGapMm: input.gapMm, correctionMm: null, message: null };
  const gap = input.gapMm;
  if (rule.contactAllowed) {
    return { rule, status: "ok", measuredGapMm: gap, correctionMm: null, message: null };
  }
  if (input.touching || (gap !== null && gap <= 1)) {
    const target = rule.typicalMm[0];
    return {
      rule,
      status: "contact_not_allowed",
      measuredGapMm: gap,
      correctionMm: target - Math.max(0, gap ?? 0),
      message: `${input.upperLabel} está apoiado em ${input.lowerLabel}. ${rule.summary} Eleve o mesmo elemento em pelo menos ${Math.round(target - Math.max(0, gap ?? 0))} mm (folga-alvo ${target} mm).`,
    };
  }
  if (gap !== null && gap < rule.minimumMm - 1) {
    const target = rule.typicalMm[0];
    return {
      rule,
      status: "below_minimum",
      measuredGapMm: gap,
      correctionMm: target - gap,
      message: `A folga de ${Math.round(gap)} mm entre ${input.upperLabel} e ${input.lowerLabel} fica abaixo do mínimo profissional de ${rule.minimumMm} mm (típico ${rule.typicalMm[0]}–${rule.typicalMm[1]} mm). Eleve o mesmo elemento em ${Math.round(target - gap)} mm.`,
    };
  }
  return { rule, status: "ok", measuredGapMm: gap, correctionMm: null, message: null };
}

/**
 * Coerência de um contrato espacial do plano com a tipologia. Devolve as
 * objeções em linguagem de projeto; lista vazia significa contrato coerente.
 */
export function spatialContractIssues(input: {
  subject: string;
  reference: string;
  relation: "above" | "below" | "beside" | "aligned" | "centered" | "inside" | "against" | "stacked" | "custom";
  support: "floor" | "wall" | "ceiling" | "element" | "unconstrained";
  clearanceMm: { minimum: number; target: number; maximum: number } | null;
  allowContact: boolean;
  source: "user" | "reference" | "catalog" | "scene" | "skill" | "inferred";
  subjectDescriptor?: TypologyDescriptor;
  referenceDescriptor?: TypologyDescriptor;
}): string[] {
  const issues: string[] = [];
  const subjectTypology = classifyInstallationTypology(input.subjectDescriptor ?? { name: input.subject });
  const referenceTypology = classifyInstallationTypology(input.referenceDescriptor ?? { name: input.reference });
  const suspendedSupport = input.support === "wall" || input.support === "ceiling";
  const vertical = input.relation === "above" || input.relation === "below" || input.relation === "stacked";

  const rule = input.relation === "above" || input.relation === "stacked"
    ? verticalClearanceRule({ upper: subjectTypology.kind, lower: referenceTypology.kind, relation: input.relation, support: input.support })
    : input.relation === "below"
      ? verticalClearanceRule({ upper: referenceTypology.kind, lower: subjectTypology.kind, relation: "above", support: input.support })
      : null;
  // What the typology says about contact for this pair when it is NOT stacked:
  // a panel may touch the rack, a countertop rests on the base, but a
  // suspended box, mirror, hood or pendant never rests on what is below.
  const freeRule = input.relation === "below"
    ? verticalClearanceRule({ upper: referenceTypology.kind, lower: subjectTypology.kind, relation: "above", support: "wall" })
    : verticalClearanceRule({ upper: subjectTypology.kind, lower: referenceTypology.kind, relation: "above", support: "wall" });
  const contactForbidden = freeRule ? !freeRule.contactAllowed : isSuspendedTypology(subjectTypology.kind);

  if (vertical && input.relation !== "stacked" && suspendedSupport && input.allowContact && contactForbidden) {
    issues.push(
      `O contrato de “${input.subject}” diz que o elemento é fixado ${input.support === "wall" ? "na parede" : "no teto"} e ao mesmo tempo permite contato com “${input.reference}”. ${subjectTypology.label === LABELS.unknown ? "Um elemento suspenso" : `Um ${subjectTypology.label}`} não apoia no que está abaixo: use uma folga profissional (allowContact=false) ou, se o briefing pedir uma torre, declare relation=stacked com support=element.`,
    );
  }

  if (input.relation === "above" && input.clearanceMm && input.clearanceMm.maximum <= 1 && suspendedSupport && !input.allowContact && contactForbidden) {
    issues.push(
      `O contrato de “${input.subject}” fixa folga 0 mm sobre “${input.reference}” para um elemento fixado ${input.support === "wall" ? "na parede" : "no teto"}: isso é empilhamento, não instalação suspensa. Escolha uma folga profissional ou declare relation=stacked com support=element.`,
    );
  }

  if (input.relation === "stacked" && contactForbidden && isSuspendedTypology(subjectTypology.kind) && !["user", "reference", "catalog"].includes(input.source)) {
    issues.push(
      `“${input.subject}” é ${subjectTypology.label}: essa tipologia é fixada na parede ou no teto com vão livre sobre “${input.reference}”. Empilhar só vale quando o briefing, a referência ou o catálogo definem uma torre ou apoio estrutural (source user/reference/catalog); caso contrário use relation=above, o suporte real e a folga profissional (${describeRule(freeRule)}).`,
    );
  }

  // A gap the designer fixed explicitly (briefing or reference) is theirs to
  // decide; the typology minimum then informs, but does not veto. Physical
  // incoherence above still applies.
  const designerFixed = input.source === "user" || input.source === "reference";
  if (rule && !rule.contactAllowed && input.clearanceMm && input.relation !== "stacked" && !designerFixed) {
    if (input.clearanceMm.maximum < rule.minimumMm) {
      issues.push(
        `A folga máxima de ${input.clearanceMm.maximum} mm entre “${input.subject}” e “${input.reference}” fica abaixo do mínimo profissional de ${rule.minimumMm} mm para ${subjectTypology.label} sobre ${referenceTypology.label} (${describeRule(rule)}). Reveja a decisão espacial antes de executar.`,
      );
    } else if (input.clearanceMm.target < rule.minimumMm) {
      issues.push(
        `A folga-alvo de ${input.clearanceMm.target} mm entre “${input.subject}” e “${input.reference}” fica abaixo do mínimo profissional de ${rule.minimumMm} mm para ${subjectTypology.label} (${describeRule(rule)}). Ajuste a folga-alvo para dentro da faixa típica.`,
      );
    }
  }
  if (rule && !rule.contactAllowed && !input.clearanceMm && (input.relation === "above" || input.relation === "below")) {
    issues.push(
      `A relação vertical de “${input.subject}” com “${input.reference}” precisa de folga declarada: ${describeRule(rule)}.`,
    );
  }
  return [...new Set(issues)];
}

export function describeRule(rule: VerticalClearanceRule | null): string {
  if (!rule) return "sem faixa consolidada; registre a folga funcional escolhida";
  if (rule.contactAllowed) return "empilhamento com apoio direto";
  const bottom = rule.bottomHeightMm ? `; face inferior a ${rule.bottomHeightMm[0]}–${rule.bottomHeightMm[1]} mm do piso` : "";
  return `mínimo ${rule.minimumMm} mm, típico ${rule.typicalMm[0]}–${rule.typicalMm[1]} mm${bottom}`;
}
